import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { jobsAPI } from '../../services/api';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import Input from '../../components/UI/Input';
import { Star, Download, History, FileText, Search, Filter, ChevronLeft, ChevronRight, Mail, Phone, Calendar, User } from 'lucide-react';
import { toast } from 'react-toastify';
import Modal from '../../components/UI/Modal';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1.5rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Heading = styled.h2`
  margin: 0;
  color: ${p => p.theme.colors.text.primary};
  font-size: ${p => p.theme.typography.fontSize['2xl']};
  font-weight: ${p => p.theme.typography.fontWeight.semibold};
`;

const Subtitle = styled.p`
  margin: 0;
  color: ${p => p.theme.colors.text.secondary};
  font-size: ${p => p.theme.typography.fontSize.sm};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
`;

const StatCard = styled(Card)`
  padding: 1.5rem;
  text-align: center;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: ${p => p.theme.typography.fontWeight.bold};
  color: ${p => p.theme.colors.primary};
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: ${p => p.theme.typography.fontSize.sm};
  color: ${p => p.theme.colors.text.secondary};
  font-weight: ${p => p.theme.typography.fontWeight.medium};
`;

const FiltersBar = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 1rem;
  background: ${p => p.theme.colors.background.secondary};
  border-radius: ${p => p.theme.borderRadius.md};
  border: 1px solid ${p => p.theme.colors.border.light};
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid ${p => p.theme.colors.border.medium};
  border-radius: ${p => p.theme.borderRadius.md};
  background: ${p => p.theme.colors.white};
  color: ${p => p.theme.colors.text.primary};
  font-size: ${p => p.theme.typography.fontSize.sm};
  cursor: pointer;
  min-width: 180px;
  
  &:focus {
    outline: none;
    border-color: ${p => p.theme.colors.primary};
    box-shadow: 0 0 0 3px ${p => p.theme.colors.primary}15;
  }
`;

const ApplicationCard = styled(Card)`
  padding: 1.75rem;
  margin-bottom: 1rem;
  transition: all 0.2s ease;
  border: 1px solid ${p => p.theme.colors.border.light};

  &:hover {
    box-shadow: ${p => p.theme.shadows.md};
    border-color: ${p => p.theme.colors.primary}40;
  }
`;

const ApplicationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 1.25rem;
  gap: 1rem;
`;

const ApplicantInfo = styled.div`
  flex: 1;
`;

const ApplicantName = styled.div`
  font-size: ${p => p.theme.typography.fontSize.lg};
  font-weight: ${p => p.theme.typography.fontWeight.semibold};
  color: ${p => p.theme.colors.text.primary};
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ApplicantDetail = styled.div`
  font-size: ${p => p.theme.typography.fontSize.sm};
  color: ${p => p.theme.colors.text.secondary};
  margin-top: 0.35rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StatusSelect = styled.select`
  padding: 0.625rem 0.875rem;
  border: 1px solid ${p => p.theme.colors.border.medium};
  border-radius: ${p => p.theme.borderRadius.md};
  background: ${p => p.theme.colors.white};
  color: ${p => p.theme.colors.text.primary};
  font-size: ${p => p.theme.typography.fontSize.sm};
  cursor: pointer;
  font-weight: ${p => p.theme.typography.fontWeight.medium};
  
  &:focus {
    outline: none;
    border-color: ${p => p.theme.colors.primary};
    box-shadow: 0 0 0 3px ${p => p.theme.colors.primary}15;
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.4rem 0.875rem;
  border-radius: ${p => p.theme.borderRadius.full};
  font-size: ${p => p.theme.typography.fontSize.xs};
  font-weight: ${p => p.theme.typography.fontWeight.semibold};
  background: ${p => getStatusColor(p.status).bg};
  color: ${p => getStatusColor(p.status).text};
`;

const getStatusColor = (status) => {
  const colors = {
    submitted: { bg: '#e3f2fd', text: '#1976d2' },
    under_review: { bg: '#fff3e0', text: '#f57c00' },
    shortlisted: { bg: '#f3e5f5', text: '#7b1fa2' },
    interview_scheduled: { bg: '#e8f5e9', text: '#388e3c' },
    interviewed: { bg: '#e1f5fe', text: '#0277bd' },
    offer_pending: { bg: '#fff9c4', text: '#f9a825' },
    hired: { bg: '#c8e6c9', text: '#2e7d32' },
    rejected: { bg: '#ffcdd2', text: '#c62828' },
  };
  return colors[status] || { bg: '#f5f5f5', text: '#666' };
};

const ActionsRow = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid ${p => p.theme.colors.border.light};
`;

const RatingSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: ${p => p.theme.colors.background.secondary};
  border-radius: ${p => p.theme.borderRadius.md};
`;

const StarRating = styled.div`
  display: flex;
  gap: 0.25rem;
  cursor: pointer;
`;

const StarButton = styled.button`
  background: none;
  border: none;
  color: ${p => p.filled ? '#ffc107' : '#ddd'};
  cursor: pointer;
  padding: 0;
  font-size: 1.25rem;
  transition: transform 0.1s ease;
  
  &:hover {
    color: #ffc107;
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const RatingInput = styled.input`
  width: 70px;
  padding: 0.5rem;
  border: 1px solid ${p => p.theme.colors.border.medium};
  border-radius: ${p => p.theme.borderRadius.md};
  text-align: center;
  font-size: ${p => p.theme.typography.fontSize.sm};
  font-weight: ${p => p.theme.typography.fontWeight.medium};

  &:focus {
    outline: none;
    border-color: ${p => p.theme.colors.primary};
    box-shadow: 0 0 0 3px ${p => p.theme.colors.primary}15;
  }
`;

const CoverLetter = styled.div`
  margin-top: 1rem;
  padding: 1.25rem;
  background: ${p => p.theme.colors.background.secondary};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.typography.fontSize.sm};
  color: ${p => p.theme.colors.text.secondary};
  line-height: 1.7;
  border-left: 3px solid ${p => p.theme.colors.primary};
`;

const CoverLetterTitle = styled.div`
  font-weight: ${p => p.theme.typography.fontWeight.semibold};
  color: ${p => p.theme.colors.text.primary};
  margin-bottom: 0.75rem;
  font-size: ${p => p.theme.typography.fontSize.sm};
`;

const HistoryModalContent = styled.div`
  max-height: 500px;
  overflow-y: auto;
  padding: 0.5rem;
`;

const HistoryItem = styled.div`
  padding: 1rem;
  border-bottom: 1px solid ${p => p.theme.colors.border.light};
  transition: background 0.2s ease;
  
  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${p => p.theme.colors.background.secondary};
  }
`;

const HistoryStatus = styled.div`
  font-weight: ${p => p.theme.typography.fontWeight.semibold};
  color: ${p => p.theme.colors.text.primary};
  margin-bottom: 0.5rem;
  font-size: ${p => p.theme.typography.fontSize.sm};
`;

const HistoryMeta = styled.div`
  font-size: ${p => p.theme.typography.fontSize.xs};
  color: ${p => p.theme.colors.text.secondary};
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const PaginationControls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.5rem;
  padding: 1rem;
  border-top: 1px solid ${p => p.theme.colors.border.light};
`;

const PaginationInfo = styled.span`
  font-size: ${p => p.theme.typography.fontSize.sm};
  color: ${p => p.theme.colors.text.secondary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: ${p => p.theme.colors.text.secondary};
`;

const storageBase = process.env.REACT_APP_BASE_URL || 'http://localhost:8000';

const STATUS_OPTIONS = [
  { value: 'submitted', label: 'Submitted' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'shortlisted', label: 'Shortlisted' },
  { value: 'interview_scheduled', label: 'Interview Scheduled' },
  { value: 'interviewed', label: 'Interviewed' },
  { value: 'offer_pending', label: 'Offer Pending' },
  { value: 'hired', label: 'Hired' },
  { value: 'rejected', label: 'Rejected' },
];

const JobApplications = () => {
  const { theme } = useTheme();
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [history, setHistory] = useState([]);
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [updatingRating, setUpdatingRating] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  useEffect(() => {
    fetchApplications();
    fetchStatistics();
  }, [jobId]);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const res = await jobsAPI.getApplications(jobId, {});
      const data = res.data.data || res.data;
      setApplications(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const res = await jobsAPI.getStatistics({ job_id: jobId });
      setStats(res.data);
    } catch (error) {
      console.error('Failed to load statistics:', error);
    }
  };

  const filteredApplications = useMemo(() => {
    let filtered = [...applications];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(app =>
        app.full_name?.toLowerCase().includes(query) ||
        app.email?.toLowerCase().includes(query) ||
        app.phone?.toLowerCase().includes(query)
      );
    }

    if (statusFilter) {
      filtered = filtered.filter(app => app.status === statusFilter);
    }

    return filtered;
  }, [applications, searchQuery, statusFilter]);

  const paginatedApplications = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return filteredApplications.slice(start, end);
  }, [filteredApplications, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredApplications.length / pageSize);

  const handleStatusChange = async (applicationId, newStatus) => {
    setUpdatingStatus({ ...updatingStatus, [applicationId]: true });
    try {
      const response = await jobsAPI.updateApplicationStatus(applicationId, { status: newStatus });
      toast.success(response.data?.message || 'Status updated successfully');
      fetchApplications();
      fetchStatistics();
    } catch (error) {
      console.error('Status update error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update status';
      toast.error(errorMessage);
    } finally {
      setUpdatingStatus({ ...updatingStatus, [applicationId]: false });
    }
  };

  const handleRatingChange = async (applicationId, rating) => {
    setUpdatingRating({ ...updatingRating, [applicationId]: true });
    try {
      await jobsAPI.updateApplicationRating(applicationId, { rating: parseFloat(rating) });
      toast.success('Rating updated successfully');
      fetchApplications();
      fetchStatistics();
    } catch (error) {
      toast.error('Failed to update rating');
    } finally {
      setUpdatingRating({ ...updatingRating, [applicationId]: false });
    }
  };

  const handleViewHistory = async (application) => {
    setSelectedApp(application);
    setShowHistoryModal(true);
    try {
      const res = await jobsAPI.getApplicationHistory(application.id);
      setHistory(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('History fetch error:', error);
      toast.error('Failed to load history');
      setHistory([]);
    }
  };

  const fileUrl = (cv_path) => {
    if (!cv_path) return '';
    if (cv_path.startsWith('http')) return cv_path;
    return `${storageBase}/storage/${cv_path}`;
  };

  const renderStars = (rating, applicationId, onChange) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <StarButton
          key={i}
          filled={i <= Math.round((rating || 0) / 2)}
          onClick={() => onChange(applicationId, i * 2)}
          type="button"
        >
          ★
        </StarButton>
      );
    }
    return stars;
  };

  return (
    <Container theme={theme}>
      <Header theme={theme}>
        <HeaderLeft>
          <Heading theme={theme}>Job Applications</Heading>
          <Subtitle theme={theme}>
            Review and manage applications for this position
          </Subtitle>
        </HeaderLeft>
        <Button
          variant="ghost"
          onClick={() => navigate('/app/jobs')}
        >
          ← Back to Jobs
        </Button>
      </Header>

      {stats && (
        <StatsGrid>
          <StatCard theme={theme}>
            <StatValue theme={theme}>{stats.total_applications || 0}</StatValue>
            <StatLabel theme={theme}>Total Applications</StatLabel>
          </StatCard>
          <StatCard theme={theme}>
            <StatValue theme={theme}>{stats.average_rating ? stats.average_rating.toFixed(1) : 'N/A'}</StatValue>
            <StatLabel theme={theme}>Average Rating</StatLabel>
          </StatCard>
          {stats.status_breakdown?.map((status) => (
            <StatCard key={status.status} theme={theme}>
              <StatValue theme={theme}>{status.count}</StatValue>
              <StatLabel theme={theme}>{status.label}</StatLabel>
            </StatCard>
          ))}
        </StatsGrid>
      )}

      <FiltersBar theme={theme}>
        <Input
          placeholder="Search by name, email, or phone..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }}
          leftIcon={<Search size={16} />}
          style={{ flex: 1, minWidth: '250px' }}
        />
        <Select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }}
          theme={theme}
        >
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </FiltersBar>

      {loading && (
        <div style={{ textAlign: 'center', padding: '3rem', color: theme.colors.text.secondary }}>
          Loading applications...
        </div>
      )}

      {applications.length === 0 && !loading && (
        <EmptyState theme={theme}>
          <FileText size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
          <h3 style={{ margin: '0 0 0.5rem 0', color: theme.colors.text.primary }}>
            No applications yet
          </h3>
          <p style={{ margin: 0 }}>Applications will appear here once candidates start applying.</p>
        </EmptyState>
      )}

      {filteredApplications.length === 0 && applications.length > 0 && !loading && (
        <EmptyState theme={theme}>
          <Search size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
          <h3 style={{ margin: '0 0 0.5rem 0', color: theme.colors.text.primary }}>
            No applications match your filters
          </h3>
          <p style={{ margin: 0 }}>Try adjusting your search or filter criteria.</p>
        </EmptyState>
      )}

      {paginatedApplications.map(app => (
        <ApplicationCard key={app.id} theme={theme}>
          <ApplicationHeader>
            <ApplicantInfo>
              <ApplicantName theme={theme}>
                <User size={18} />
                {app.full_name}
              </ApplicantName>
              <ApplicantDetail theme={theme}>
                <Mail size={14} />
                {app.email}
              </ApplicantDetail>
              {app.phone && (
                <ApplicantDetail theme={theme}>
                  <Phone size={14} />
                  {app.phone}
                </ApplicantDetail>
              )}
              <ApplicantDetail theme={theme}>
                <Calendar size={14} />
                Submitted: {app.submitted_at ? new Date(app.submitted_at).toLocaleString() : 'N/A'}
              </ApplicantDetail>
            </ApplicantInfo>

            <StatusBadge status={app.status} theme={theme}>
              {STATUS_OPTIONS.find(s => s.value === app.status)?.label || app.status}
            </StatusBadge>
          </ApplicationHeader>

          <ActionsRow theme={theme}>
            <StatusSelect
              value={app.status}
              onChange={(e) => handleStatusChange(app.id, e.target.value)}
              disabled={updatingStatus[app.id]}
              theme={theme}
            >
              {STATUS_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </StatusSelect>

            <RatingSection theme={theme}>
              <span style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary }}>
                Rating:
              </span>
              <StarRating>
                {renderStars(app.rating || 0, app.id, handleRatingChange)}
              </StarRating>
              <RatingInput
                type="number"
                min="0"
                max="10"
                step="0.1"
                value={app.rating || ''}
                onChange={(e) => handleRatingChange(app.id, e.target.value)}
                disabled={updatingRating[app.id]}
                theme={theme}
                placeholder="0-10"
              />
            </RatingSection>

            <Button
              variant="outline"
              size="small"
              onClick={() => window.open(fileUrl(app.cv_path), '_blank')}
              leftIcon={<Download size={16} />}
            >
              Download CV
            </Button>

            <Button
              variant="ghost"
              size="small"
              onClick={() => handleViewHistory(app)}
              leftIcon={<History size={16} />}
            >
              View History
            </Button>
          </ActionsRow>

          {app.interview_scheduled_at && (
            <div style={{
              marginTop: '1rem',
              padding: '0.75rem',
              background: theme.colors.primary + '15',
              borderRadius: theme.borderRadius.md,
              fontSize: theme.typography.fontSize.sm,
              color: theme.colors.primary,
              fontWeight: theme.typography.fontWeight.medium
            }}>
              📅 Interview Scheduled: {new Date(app.interview_scheduled_at).toLocaleString()}
            </div>
          )}

          {app.cover_letter && (
            <CoverLetter theme={theme}>
              <CoverLetterTitle theme={theme}>Cover Letter</CoverLetterTitle>
              <div>{app.cover_letter}</div>
            </CoverLetter>
          )}
        </ApplicationCard>
      ))}

      {totalPages > 1 && (
        <PaginationControls theme={theme}>
          <PaginationInfo theme={theme}>
            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, filteredApplications.length)} of {filteredApplications.length} applications
          </PaginationInfo>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <Button
              size="small"
              variant="ghost"
              leftIcon={<ChevronLeft size={16} />}
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
              Page {currentPage} of {totalPages}
            </span>
            <Button
              size="small"
              variant="ghost"
              rightIcon={<ChevronRight size={16} />}
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        </PaginationControls>
      )}

      <Modal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        title={`Status History - ${selectedApp?.full_name}`}
        size="medium"
      >
        <HistoryModalContent theme={theme}>
          {history.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: theme.colors.text.secondary }}>
              No history available for this application.
            </div>
          ) : (
            history.map((item, idx) => (
              <HistoryItem key={idx} theme={theme}>
                <HistoryStatus theme={theme}>
                  {item.old_status ? `${item.old_status} → ${item.new_status}` : item.new_status}
                </HistoryStatus>
                <HistoryMeta theme={theme}>
                  <div>{new Date(item.created_at).toLocaleString()}</div>
                  {item.changed_by?.name && (
                    <div>Changed by: {item.changed_by.name}</div>
                  )}
                  {item.notes && (
                    <div style={{ marginTop: '0.5rem', fontStyle: 'italic' }}>
                      {item.notes}
                    </div>
                  )}
                </HistoryMeta>
              </HistoryItem>
            ))
          )}
        </HistoryModalContent>
      </Modal>
    </Container>
  );
};

export default JobApplications;
